import { useEffect, useRef } from 'react';

export interface Hotkey {
  /** Lower-case key name, e.g. "1", "arrowleft", "z". */
  key: string;
  meta?: boolean;
  shift?: boolean;
  run: (event: KeyboardEvent) => void;
}

/**
 * Binds keys at the window level, ignoring anything typed into a field.
 *
 * Culling is a keyboard loop, so bindings live here rather than on focused
 * elements — the photographer never has to think about what has focus.
 */
export function useHotkeys(bindings: Hotkey[], enabled = true): void {
  const ref = useRef(bindings);
  ref.current = bindings;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      const meta = event.metaKey || event.ctrlKey;

      for (const binding of ref.current) {
        if (binding.key !== key) continue;
        if (!!binding.meta !== meta) continue;
        if (binding.shift !== undefined && binding.shift !== event.shiftKey) continue;

        event.preventDefault();
        binding.run(event);
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
